import { SectionTab } from "polotno/side-panel";
import { FaCheckSquare } from "react-icons/fa";
import { TAny } from "../../shared/types/common";
import { observer } from "mobx-react-lite";
import { FlexContainer } from "../transcript/elements/CommonStyle";
import { useRef, useState } from "react";
import { LearningModeContainer } from "./LearningModeContainer";
import { StoreType } from "polotno/model/store";
import { TranscriptApi } from "../../shared/services/transcript.api";
import { Button } from "@blueprintjs/core";
import { styled } from "styled-components";
import { Search, Mic } from "lucide-react";

interface ISpeechRecognition extends EventTarget {
  start(): void;
  stop(): void;
  abort(): void;
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((this: ISpeechRecognition, ev: any) => any) | null;
  onerror: ((this: ISpeechRecognition, ev: any) => any) | null;
}

declare global {
  interface Window {
    webkitSpeechRecognition: {
      new (): ISpeechRecognition;
    };
    SpeechRecognition: {
      new (): ISpeechRecognition;
    };
  }
}

export const LearningModeTab = {
  name: "learning-mode-panel",
  Tab: (props: TAny) => (
    <SectionTab name="Quiz test" {...props}>
      <FaCheckSquare />
    </SectionTab>
  ),
  Panel: observer(({ store }: { store: StoreType }) => {
    const [data, setData] = useState([]);
    const [isGenerating, setIsGenerating] = useState(false);
    const processId = store.custom?.processId;
    const [searchText, setSearchText] = useState("");
    const [isSearching, setIsSearching] = useState(false);
    const [semanticSearchResult, setSemanticSearchResult] = useState("");
    const [isListening, setIsListening] = useState(false);
    const [isVoiceLoading, setIsVoiceLoading] = useState(false);
    const recognitionRef = useRef<ISpeechRecognition | null>(null);

    const fetchData = async () => {
      setIsGenerating(true);
      try {
        const body = { processId, text: "What is project" };
        const response = await TranscriptApi.genLearningMode(body);
        if (response.success && response.segments.length > 0) {
          setData(response.segments as never);
        }
      } catch (error) {
        console.error("Error processing video:", error);
      } finally {
        setIsGenerating(false);
      }
    };

    const semanticVideoSearch = async () => {
      setIsSearching(true);
      try {
        const body = { processId, text: searchText };
        const response = await TranscriptApi.semanticVideoSearch(body);
        if (response.success && response.response.answer.length > 0) {
          setSemanticSearchResult(response.response.answer as never);
        }
      } catch (error) {
        console.error("Error processing video:", error);
      } finally {
        setIsSearching(false);
      }
    };

    const toggleListening = () => {
      const SpeechRecognition =
        window.SpeechRecognition || window.webkitSpeechRecognition;

      if (!SpeechRecognition) {
        alert("Speech Recognition is not supported in this browser.");
        return;
      }

      if (!recognitionRef.current) {
        const recognition = new SpeechRecognition();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = "en-US";

        recognition.onresult = (event: any) => {
          let interimTranscript = "";
          for (let i = event.resultIndex; i < event.results.length; i++) {
            interimTranscript += event.results[i][0].transcript;
          }
          setSearchText(interimTranscript);
        };

        recognition.onerror = (event: any) => {
          console.error("Speech recognition error:", event.error);
          setIsListening(false);
          setIsVoiceLoading(false);
        };

        recognitionRef.current = recognition;
      }

      if (!isListening) {
        setIsVoiceLoading(true);
        recognitionRef.current.start();
        setTimeout(() => {
          setIsListening(true);
          setIsVoiceLoading(false);
        }, 500);
      } else {
        recognitionRef.current.stop();
        setIsListening(false);
      }
    };

    return (
      <div>
        <FlexContainer>
          <h3>Quiz test</h3>
        </FlexContainer>
        <Button
          icon="document"
          text={`Quiz test`}
          intent="primary"
          loading={isGenerating}
          onClick={fetchData}
          disabled={!processId || isGenerating || isSearching}
        />
        <SearchContainer>
          <InputWrapper>
            <SearchInput
              placeholder="Ask a question..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
            <MicIcon
              className="mic-icon"
              onClick={toggleListening}
              type="button"
            >
              <Mic size={16} color="#1a73e8" />
            </MicIcon>
            <p style={{ font: "italic" }}>
              {isVoiceLoading
                ? "Initializing..."
                : isListening
                  ? "Listening..."
                  : ""}
            </p>
          </InputWrapper>
          <SearchButton
            intent="primary"
            onClick={semanticVideoSearch}
            type="button"
            disabled={!processId || isGenerating || !searchText}
            loading={isSearching}
          >
            <Search size={16} style={{ color: "white" }} />
          </SearchButton>
        </SearchContainer>
        {(data.length > 0 || semanticSearchResult.length > 0) && (
          <LearningModeContainer
            qnaData={data}
            semanticSearchResult={semanticSearchResult}
          />
        )}
      </div>
    );
  }),
};

const SearchContainer = styled.div`
  display: flex;
  width: 300px;
  font-family: sans-serif;
  margin-top: 30px;
  height: 60px;
`;

const InputWrapper = styled.div`
  position: relative;
  flex: 1;
  /* height: 40px; */
`;

const SearchInput = styled.textarea`
  width: 100%;
  padding: 8px 32px 8px 10px;
  border: 1px solid #ccc;
  border-right: none;
  border-radius: 4px 0 0 4px;
  outline: none;
  font-size: 14px;
  box-sizing: border-box;
  height: 60px;
`;

const MicIcon = styled.button`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
`;

const SearchButton = styled(Button)`
  width: 40px;
  border-left: none;
  border-radius: 0 4px 4px 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;
